import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Zap, Loader2, Plus, Trash2, Save, Clock, AlertTriangle } from "lucide-react";

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const toHHMM = (m) => {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};
const fromHHMM = (s) => {
  const [h, m] = (s || "00:00").split(":").map((x) => parseInt(x, 10) || 0);
  return h * 60 + m;
};

export default function AdminSurge() {
  const [cfg, setCfg] = useState(null);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: now }] = await Promise.all([
        api.get("/surge/config"),
        api.get("/surge/current"),
      ]);
      // Inject stable client-side IDs for React keys
      c.windows = (c.windows || []).map((w, i) => ({ ...w, _cid: w._cid || `w-${Date.now()}-${i}` }));
      setCfg(c);
      setCurrent(now);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load surge config");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); const id = setInterval(load, 60000); return () => clearInterval(id); }, []);

  const setField = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const updateWindow = (i, patch) => setCfg((c) => {
    const windows = [...(c.windows || [])];
    windows[i] = { ...windows[i], ...patch };
    return { ...c, windows };
  });

  const toggleDay = (i, day) => setCfg((c) => {
    const windows = [...(c.windows || [])];
    const cur = new Set(windows[i].days || []);
    if (cur.has(day)) cur.delete(day); else cur.add(day);
    windows[i] = { ...windows[i], days: [...cur].sort() };
    return { ...c, windows };
  });

  const addWindow = () => setCfg((c) => ({
    ...c,
    windows: [...(c.windows || []), { _cid: `w-${Date.now()}-${(c.windows || []).length}`, days: [0, 1, 2, 3, 4], start_min: 8 * 60, end_min: 10 * 60, multiplier: 1.2, label: "New Window" }],
  }));

  const removeWindow = (i) => setCfg((c) => ({ ...c, windows: c.windows.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        active: !!cfg.active,
        cap_multiplier: Number(cfg.cap_multiplier) || 2.0,
        min_multiplier: Number(cfg.min_multiplier) || 1.0,
        windows: (cfg.windows || []).map((w) => ({
          days: (w.days || []).map((d) => Number(d)),
          start_min: Number(w.start_min) || 0,
          end_min: Number(w.end_min) || 0,
          multiplier: Number(w.multiplier) || 1.0,
          label: w.label || "",
        })),
      };
      const { data } = await api.put("/surge/config", payload);
      setCfg(data);
      toast.success("Surge config saved");
      const { data: now } = await api.get("/surge/current");
      setCurrent(now);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading || !cfg) {
    return <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto" /></div>;
  }

  return (
    <div className="space-y-6 animate-fadeInUp" data-testid="admin-surge-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Zap className="h-7 w-7 text-[#FF6B00]" /> Surge Pricing
          </h1>
          <p className="text-sm text-slate-500 mt-1">Charge a time-based multiplier during peak demand windows. Applies to all customer-facing delivery quotes.</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#FF6B00] hover:bg-[#E65A00] rounded-xl h-11 px-5" data-testid="save-surge-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Save Config</>}
        </Button>
      </div>

      {/* Current Status */}
      <div className={`rounded-2xl border-2 p-5 ${current?.active ? "bg-orange-50 border-[#FF6B00]/40" : "bg-slate-50 border-slate-200"}`} data-testid="surge-current-status">
        <div className="flex items-center gap-3 flex-wrap">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${current?.active ? "bg-[#FF6B00] text-white" : "bg-white text-slate-400 border border-slate-200"}`}>
            <Zap className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="font-['Outfit'] font-bold text-slate-900">
              {current?.active ? `Surge Active — ${current.label}` : "No active surge"}
            </div>
            <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
              <Clock className="h-3 w-3" /> IST: {current?.now_ist ? new Date(current.now_ist).toLocaleTimeString() : "--"} · Current multiplier: <span className="font-bold text-slate-900">×{Number(current?.multiplier || 1).toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Global */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6">
        <div className="font-['Outfit'] font-bold text-slate-900 mb-4">Global Settings</div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
            <Switch checked={!!cfg.active} onCheckedChange={(v) => setField("active", v)} data-testid="surge-active-toggle" />
            <div>
              <div className="text-sm font-semibold text-slate-900">Engine enabled</div>
              <div className="text-[11px] text-slate-500">Master on/off</div>
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700">Cap multiplier</Label>
            <Input type="number" step="0.1" min="1" max="3" value={cfg.cap_multiplier} onChange={(e) => setField("cap_multiplier", parseFloat(e.target.value))} className="rounded-xl h-10 mt-1" data-testid="surge-cap-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700">Min multiplier</Label>
            <Input type="number" step="0.1" min="1" max="3" value={cfg.min_multiplier} onChange={(e) => setField("min_multiplier", parseFloat(e.target.value))} className="rounded-xl h-10 mt-1" data-testid="surge-min-input" />
          </div>
        </div>
      </div>

      {/* Windows */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-['Outfit'] font-bold text-slate-900">Peak Windows</div>
          <Button onClick={addWindow} variant="outline" className="rounded-xl h-9 text-xs" data-testid="add-surge-window-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add window
          </Button>
        </div>
        {(cfg.windows || []).length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400 flex flex-col items-center gap-2">
            <AlertTriangle className="h-6 w-6" />
            No peak windows configured. Surge will never apply.
          </div>
        ) : (
          <div className="space-y-4">
            {cfg.windows.map((w, i) => (
              <div key={w._cid || `w-${i}`} className="border border-slate-200 rounded-xl p-4 bg-slate-50/40" data-testid={`surge-window-${i}`}>
                <div className="grid sm:grid-cols-12 gap-3 items-end">
                  <div className="sm:col-span-3">
                    <Label className="text-[10px] font-semibold text-slate-500 uppercase">Label</Label>
                    <Input value={w.label || ""} onChange={(e) => updateWindow(i, { label: e.target.value })} className="rounded-lg h-9 mt-1" data-testid={`surge-window-label-${i}`} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-[10px] font-semibold text-slate-500 uppercase">Start</Label>
                    <Input type="time" value={toHHMM(w.start_min)} onChange={(e) => updateWindow(i, { start_min: fromHHMM(e.target.value) })} className="rounded-lg h-9 mt-1" data-testid={`surge-window-start-${i}`} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-[10px] font-semibold text-slate-500 uppercase">End</Label>
                    <Input type="time" value={toHHMM(w.end_min)} onChange={(e) => updateWindow(i, { end_min: fromHHMM(e.target.value) })} className="rounded-lg h-9 mt-1" data-testid={`surge-window-end-${i}`} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-[10px] font-semibold text-slate-500 uppercase">Multiplier</Label>
                    <Input type="number" step="0.05" min="1" max="3" value={w.multiplier} onChange={(e) => updateWindow(i, { multiplier: parseFloat(e.target.value) })} className="rounded-lg h-9 mt-1" data-testid={`surge-window-multiplier-${i}`} />
                  </div>
                  <div className="sm:col-span-3 flex justify-end">
                    <Button variant="outline" onClick={() => removeWindow(i)} className="rounded-lg h-9 text-xs border-red-200 text-red-600 hover:bg-red-50" data-testid={`remove-surge-window-${i}`}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                    </Button>
                  </div>
                </div>
                <div className="mt-3">
                  <Label className="text-[10px] font-semibold text-slate-500 uppercase">Days</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {DAY_NAMES.map((d, idx) => {
                      const on = (w.days || []).includes(idx);
                      return (
                        <button
                          key={d}
                          type="button"
                          onClick={() => toggleDay(i, idx)}
                          className={`px-3 py-1 rounded-lg text-[11px] font-semibold border transition ${
                            on ? "bg-[#FF6B00] text-white border-[#FF6B00]" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
                          }`}
                          data-testid={`surge-day-${i}-${idx}`}
                        >
                          {d}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
